import React, { useState, useMemo, useEffect } from "react";
import { LeadViewEditViewFullInfoProps } from "./lead-view-edit-view-full-info.props";
import { Box } from "@mui/material";
import { Formik, Form } from "formik";
import {
  MARITAL_STATUS_OPTIONS,
  RELATIONSHIP_OPTIONS
} from "../../../../../../constants/leadDropdown";
import leadInfoIcon from "../../../../../../assets/icons/leadInfo.svg";
import {
  TextInputComponent,
  SelectInputComponent,
  DescriptionInputComponent,
  RevenueInputComponent,
} from "../../../../../../common/components";
import { updateLeadDetails } from "../../../../../../api/lead";
import "./lead-view-edit-view-full-info.scss";
import { validationSchema } from "./lead-view-edit-view-full-info.validate";
import isEqual from "lodash/isEqual";
import CountryCodeWithPhoneNumber from "../../../../../../common/components/phone-number-input-component/country-code-phone-numner-input";
import { isOnlyCountryCode } from "../../../../../../utils/utils";
import { useLogin } from "../../../../../../common/hooks/useUser";
import dayjs from "dayjs";

const LeadViewEditViewFullInfo: React.FC<LeadViewEditViewFullInfoProps> = (
  props: any
) => {
  const {
    leadDetails,
    setViewFullInfoOpen,
    selectedLeadId,
    handleCancelUpdate,
    setAlertType,
    setAlertMessage,
    setAlertOpen,
    setLoading,
    uploadedImageFile,
    removeImage,
    resetImageUpload,
    handleFormSubmitError,
    isMobile,
  } = props;
  const formikRef: any = React.useRef(null);
  const [isViewOpen, setIsViewOpen] = useState<boolean>(true);
  const { tenantDetails } = useLogin();

  const checkDisableField = () => {
    return tenantDetails?.tenantType === "SuperTenant" &&
      leadDetails?.assigned_to &&
      !(leadDetails?.lead_status === "Closed-Lost")
      ? true
      : false;
  };
  const sanitize = (value: any): string => {
    return value === null || value === undefined || value === "null"
      ? ""
      : value;
  };

  const initialValues = useMemo(
    () => ({
      title: sanitize(leadDetails?.title),
      assignedPartner: sanitize(leadDetails?.assigned_to),
      firstName: sanitize(leadDetails?.first_name),
      lastName: sanitize(leadDetails?.last_name),
      email: sanitize(leadDetails?.primary_email_address),
      secondaryEmail: sanitize(leadDetails?.secondary_email_address),
      fax: sanitize(leadDetails?.fax_number),
      website: sanitize(leadDetails?.website_url),
      numEmployees: sanitize(leadDetails?.number_of_employees),
      annualRevenue: sanitize(leadDetails?.annual_revenue),
      socialLeadId: sanitize(leadDetails?.social_lead_id),
      streetAddress: sanitize(leadDetails?.address?.street),
      description: sanitize(leadDetails?.description),
      city: sanitize(leadDetails?.address?.city),
      state: sanitize(leadDetails?.address?.state),
      country: sanitize(leadDetails?.address?.country),
      zipcode: sanitize(leadDetails?.address?.zipcode),
      addressId: sanitize(leadDetails?.address?.id),
      interestedProduct: Array.isArray(leadDetails?.interested_products)
        ? leadDetails.interested_products
        : typeof leadDetails?.interested_products === "string" &&
            leadDetails?.interested_products !== "null"
          ? leadDetails.interested_products.split(",").map((s: any) => s.trim())
          : [],
      selectedLeadOwner: leadDetails?.createdBy?.first_name ?? null,
      selectedLeadSource: leadDetails?.lead_source ?? null,
      selectedLeadStatus: leadDetails?.lead_status ?? null,
      rating: sanitize(leadDetails?.confidence_score) ?? null,
      industry: sanitize(leadDetails?.industry) ?? null,
      primaryPhoneNumber: sanitize(leadDetails.primary_phone_number) || null,
      secondaryPhoneNumber:
        sanitize(leadDetails.secondary_phone_number) || null,
      designation: leadDetails?.designation || "",
      middleName: leadDetails?.middle_name || null,
      age: leadDetails?.age || "",
      leadValue: leadDetails?.lead_value || "",
      maritalStatus: leadDetails?.marital_status || null,
      assignedTo: leadDetails?.assigned_to || null,
      contactPerson: leadDetails?.contact_person || "",
      relationship: leadDetails?.relationship || "",
      contactPersonPhoneNumber: leadDetails?.contact_person_phone_number || "",
      company: leadDetails?.company || "",
      deathDate: leadDetails?.death_date
        ? dayjs(leadDetails?.death_date)
        : null,
    }),
    [leadDetails]
  );

  const handleCancel = () => {
    resetImageUpload?.();
    setViewFullInfoOpen(false);
  };

  const handleSave = async (values: any) => {
    setLoading(true);
    try {
      const formData = new FormData();
      const payload = {
        title: values.title || null,
        firstName: values.firstName,
        lastName: values.lastName || null,
        primaryEmail: values.email,
        assignedTo: values?.assignedTo || null,
        secondaryEmail: values.secondaryEmail || null,
        ...(isOnlyCountryCode(values.primaryPhoneNumber)
          ? {}
          : { primaryPhoneNumber: values.primaryPhoneNumber || null }),
        ...(isOnlyCountryCode(values.secondaryPhoneNumber)
          ? {}
          : { secondaryPhoneNumber: values.secondaryPhoneNumber || null }),
        website: values.website || null,
        address: {
          street: values.streetAddress || null,
          city: values.city || null,
          state: values.state || null,
          country: values.country || null,
          zipcode: values.zipcode || null,
        },
        leadSource: values.selectedLeadSource || null,
        leadStatus: values.selectedLeadStatus || null,
        faxNumber: isOnlyCountryCode(values.fax) ? null : values.fax || null,
        industry: values.industry || null,
        interestedProducts:
          Array.isArray(values?.interestedProduct) &&
          values?.interestedProduct.length > 0
            ? values?.interestedProduct?.join(", ")
            : null,
        socialLeadId: values.socialLeadId || null,
        description: values.description || null,
        confidenceScore: values.rating || null,
        designation: values.designation || null,
        middleName: values.middleName || null,
        age: parseInt(values.age || 0) || null,
        leadValue: values.leadValue || null,
        maritalStatus: values.maritalStatus || null,
        page: "second",
        company: values?.company || null,
        preferredContactMode: leadDetails?.preferred_contact_mode || null,
        relationship: values.relationship || null,
        contactPerson: values.contactPerson || null,
        contactPersonPhoneNumber: isOnlyCountryCode(values.contactPersonPhoneNumber) ? null : values.contactPersonPhoneNumber || null,
        deathDate: values.deathDate
          ? dayjs(values.deathDate).format("YYYY-MM-DD")
          : null,
      };

      Object.entries(payload).forEach(([key, value]) => {
        if (key !== "imageUrl" && key !== "address" && value !== null) {
          formData.append(key, value);
        }
      });
      if (uploadedImageFile) {
        formData.append("leadImg", uploadedImageFile);
      } else if (removeImage && !uploadedImageFile) {
        formData.append("removeLeadImage", "true");
      }
      ["streetAddress", "city", "state", "country", "zipcode"].forEach(
        (field) => {
          if (values[field]) {
            formData.append(
              `address[${field.replace("Address", "")}]`,
              values[field]
            );
          }
        }
      );
      if (values.addressId) {
        formData.append("address[id]", values.addressId);
      }
      if (values.imageUrl instanceof File) {
        formData.append("leadImg", uploadedImageFile);
      }
      const response = await updateLeadDetails(selectedLeadId, formData);

      if (response.data.success) {
        if (props.onLeadNameUpdate) {
          props.onLeadNameUpdate(
            `${values.firstName} ${values.lastName}`.trim()
          );
        }
        setAlertMessage("Lead updated successfully");
        setAlertType("success");
        setAlertOpen(true);
        handleCancelUpdate();
        setLoading(false);
      } else {
        setAlertMessage("Failed to save lead.");
        setAlertType("error");
        setAlertOpen(true);
        setLoading(false);
      }
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
          "An error occurred while saving the lead."
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (formikRef) {
      formikRef?.current?.validateForm();
      formikRef?.current?.validateField();
      setTimeout(() => {
        if (formikRef?.current?.isValid === false) {
          formikRef?.current?.handleSubmit();
        }
      }, 500);
    }
  }, []);

  const checkAddDetails = (errors: any) => {
    Object.keys(errors)?.forEach((key) => {
      const isFromDetailsTab = [""]?.includes(key);
      if (isFromDetailsTab) {
        handleFormSubmitError("details");
      }
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema(true)}
      onSubmit={(values) => handleSave(values)}
      validateOnChange={true}
      validateOnMount={true}
      validateOnBlur={true}
      enableReinitialize={true}
      innerRef={formikRef}
    >
      {({
        values,
        setFieldValue,
        handleSubmit,
        setFieldTouched,
        touched,
        errors,
        submitCount,
      }) => {
        const isFormDirty = !isEqual(values, initialValues);

        return (
          <Form>
            <div className="lead-list-view-details-view-box-full-info">
              {!isMobile && (
                <div className="lead-list-view-details-view-box-full-info-header">
                  <div>Additional Details</div>
                  <div>
                    {isViewOpen ? (
                      <i
                        className="fa fa-angle-down"
                        onClick={() => {
                          setIsViewOpen(!isViewOpen);
                        }}
                      />
                    ) : (
                      <i
                        className="fa fa-angle-up"
                        onClick={() => {
                          setIsViewOpen(!isViewOpen);
                        }}
                      />
                    )}
                  </div>
                </div>
              )}

              {isViewOpen ? (
                <>
                  <div className="lead-list-view-details-view-box-full-info-header-text">
                    Lead Information
                  </div>
                  <div className="lead-list-view-details-view-box-full-info-data">
                    <div className="lead-list-view-details-view-box-full-info-data-section">
                      <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="lead-list-view-details-view-box-full-info-data-section-main">
                          Lead Value
                        </div>
                        <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                          <RevenueInputComponent
                            fieldName="leadValue"
                            placeholder="Enter Lead Value"
                            optional={false}
                            disabled={checkDisableField()}
                            value={values.leadValue}
                            onChange={(e: any) => {
                              const input = e.target.value;
                              if (
                                /^\d*\.?\d*$/.test(input) &&
                                input.length <= 15
                              ) {
                                setFieldValue("leadValue", input);
                              } else if (e?.nativeEvent?.data === null) {
                                setFieldValue("leadValue", input);
                              }
                            }}
                            logo={leadInfoIcon}
                          />
                        </div>
                      </div>
                      <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="lead-list-view-details-view-box-full-info-data-section-main">
                          Marital Status
                        </div>
                        <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                          <SelectInputComponent
                            fieldName="maritalStatus"
                            options={MARITAL_STATUS_OPTIONS}
                            onChange={(e: any) => {
                              setFieldValue("maritalStatus", e);
                            }}
                            value={values?.maritalStatus}
                            placeholder="Select Marital Status"
                            disabled={checkDisableField()}
                            notEditable={checkDisableField()}
                          />
                        </div>
                      </div>
                      <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="lead-list-view-details-view-box-full-info-data-section-main">
                          Secondary Email
                        </div>
                        <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                          <TextInputComponent
                            type="email"
                            fieldName="secondaryEmail"
                            placeholder="Enter Secondary Email"
                            optional={false}
                            disabled={checkDisableField()}
                            value={values.secondaryEmail}
                            onChange={(e: any) =>
                              setFieldValue("secondaryEmail", e?.target?.value)
                            }
                          />
                        </div>
                      </div>
                      <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="lead-list-view-details-view-box-full-info-data-section-main">
                          Fax
                        </div>
                        <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                          <CountryCodeWithPhoneNumber
                            fieldName="fax"
                            placeholder={values?.fax ? "" : "Enter Fax Number"}
                            onChange={(
                              value: string,
                              country: any,
                              one: any,
                              two: any,
                            ) => {
                              const countryCode = country?.dialCode;

                              if (!countryCode) {
                                console.warn(
                                  "Missing dialCode in country object",
                                );
                                return;
                              }
                              if (`+${countryCode}` == two) {
                                setFieldValue("fax", "");
                              } else {
                                setFieldValue("fax", two);
                              }
                            }}
                            onBlur={() => {
                              setFieldTouched("fax", true);
                            }}
                            value={values?.fax || "+1"}
                            defaultCountry="us"
                            optional={true}
                            disabled={checkDisableField()}
                            // countryCodeEditable
                          />
                          {submitCount > 0 && typeof errors.fax === "string" && (
                            <div className="error-message">{errors.fax}</div>
                          )}
                        </div>
                      </div>
                      <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="lead-list-view-details-view-box-full-info-data-section-main">
                          Designation
                        </div>
                        <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                          <TextInputComponent
                            type="text"
                            fieldName="designation"
                            placeholder="Enter Designation"
                            optional={false}
                            disabled={checkDisableField()}
                            value={values.designation}
                            onChange={(e: any) =>
                              setFieldValue("designation", e?.target?.value)
                            }
                            maxlength="100"
                          />
                        </div>
                      </div>
                      <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="lead-list-view-details-view-box-full-info-data-section-main">
                          Contact Person
                        </div>
                        <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                          <TextInputComponent
                            type="text"
                            fieldName="contactPerson"
                            placeholder="Enter Contact Person"
                            optional={false}
                            disabled={checkDisableField()}
                            value={values.contactPerson}
                            onChange={(e: any) =>
                              setFieldValue("contactPerson", e?.target?.value)
                            }
                            maxlength="100"
                          />
                        </div>
                      </div>
                      <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="lead-list-view-details-view-box-full-info-data-section-main">
                          Contact Person Phone Number
                        </div>
                        <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                          <CountryCodeWithPhoneNumber
                            fieldName="contactPersonPhoneNumber"
                            placeholder={
                              values?.contactPersonPhoneNumber
                                ? ""
                                : "Enter Contact Person Phone Number"
                            }
                            onChange={(
                              value: string,
                              country: any,
                              one: any,
                              two: any,
                            ) => {
                              const countryCode = country?.dialCode;
                              if (!countryCode) {
                                console.warn(
                                  "Missing dialCode in country object",
                                );
                                return;
                              }

                              if (`+${countryCode}` == two) {
                                setFieldValue("contactPersonPhoneNumber", null);
                              } else {
                                setFieldValue("contactPersonPhoneNumber", two);
                              }
                            }}
                            onBlur={() => {
                              setFieldTouched("contactPersonPhoneNumber", true);
                            }}
                            value={values?.contactPersonPhoneNumber || "+1"}
                            defaultCountry="us"
                            optional={true}
                            disabled={checkDisableField()}
                            // countryCodeEditable
                          />
                          {submitCount > 0 &&
                            typeof errors.contactPersonPhoneNumber === "string" && (
                              <div className="error-message">
                                {errors.contactPersonPhoneNumber}
                              </div>
                            )}
                        </div>
                      </div>
                    </div>

                    <div className="lead-list-view-details-view-box-full-info-data-section">
                      <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="lead-list-view-details-view-box-full-info-data-section-main">
                          Age
                        </div>
                        <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                          <TextInputComponent
                            type="text"
                            fieldName="age"
                            placeholder="Enter Age"
                            optional={false}
                            disabled={checkDisableField()}
                            value={values.age}
                            isName={false}
                            allowSpecialCharacters={true}
                            onChange={(e: any) =>
                              setFieldValue(
                                "age",
                                e?.target?.value
                                  ? Number(e?.target?.value) > 0 &&
                                    Number(e?.target?.value) <= 120
                                    ? e?.target?.value
                                    : values.age
                                  : "",
                              )
                            }
                          />
                        </div>
                      </div>
                      <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="lead-list-view-details-view-box-full-info-data-section-main">
                          Secondary Phone Number
                        </div>
                        <div className="lead-list-view-details-view-box-full-info-data-section-sub secondary-error">
                          <CountryCodeWithPhoneNumber
                            fieldName="secondaryPhoneNumber"
                            placeholder={
                              values?.secondaryPhoneNumber
                                ? ""
                                : "Enter Secondary Phone Number"
                            }
                            onChange={(
                              value: string,
                              country: any,
                              one: any,
                              two: any,
                            ) => {
                              const countryCode = country?.dialCode;
                              if (!countryCode) {
                                console.warn(
                                  "Missing dialCode in country object",
                                );
                                return;
                              }

                              if (`+${countryCode}` == two) {
                                setFieldValue("secondaryPhoneNumber", null);
                              } else {
                                setFieldValue("secondaryPhoneNumber", two);
                              }
                            }}
                            onBlur={() => {
                              setFieldTouched("secondaryPhoneNumber", true);
                            }}
                            value={values?.secondaryPhoneNumber || "+1"}
                            defaultCountry="us"
                            optional={true}
                            disabled={checkDisableField()}
                            // countryCodeEditable
                          />{" "}
                          {submitCount > 0 &&
                            typeof errors.secondaryPhoneNumber === "string" && (
                              <div className="error-message">
                                {errors.secondaryPhoneNumber}
                              </div>
                            )}
                        </div>
                      </div>
                      <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="lead-list-view-details-view-box-full-info-data-section-main">
                          Website
                        </div>
                        <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                          <TextInputComponent
                            type="Website"
                            fieldName="website"
                            placeholder="Enter Website"
                            optional={false}
                            disabled={checkDisableField()}
                            value={values.website}
                            onChange={(e: any) =>
                              setFieldValue("website", e?.target?.value)
                            }
                            maxlength="150"
                          />
                        </div>
                      </div>
                      <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="lead-list-view-details-view-box-full-info-data-section-main">
                          Industry
                        </div>
                        <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                          <TextInputComponent
                            type="text"
                            fieldName="industry"
                            value={values.industry}
                            placeholder="Enter Industry"
                            optional={false}
                            disabled={checkDisableField()}
                            onChange={(e: any) =>
                              setFieldValue("industry", e?.target?.value)
                            }
                            maxlength="50"
                          />
                        </div>
                      </div>
                      <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="lead-list-view-details-view-box-full-info-data-section-main">
                          Social Lead ID
                        </div>
                        <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                          <TextInputComponent
                            type="text"
                            fieldName="socialLeadId"
                            placeholder="Enter Social Lead ID"
                            optional={false}
                            disabled={checkDisableField()}
                            value={values.socialLeadId}
                            onChange={(e: any) =>
                              setFieldValue("socialLeadId", e?.target?.value)
                            }
                          />
                        </div>
                      </div>
                      <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="lead-list-view-details-view-box-full-info-data-section-main">
                          Relationship
                        </div>
                        <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                          <SelectInputComponent
                            options={RELATIONSHIP_OPTIONS}
                            fieldName="relationship"
                            onChange={(e: any) => {
                              setFieldValue("relationship", e);
                            }}
                            value={values?.relationship}
                            placeholder="Select Relationship"
                            disabled={checkDisableField()}
                            optional={false}
                          />
                          {submitCount > 0 && typeof errors.relationship === 'string' && (
                            <div className="error-message">{errors.relationship}</div>
                          )}
                        </div>
                      </div>
                      <div className="lead-list-view-details-view-box-full-info-data-section-main-sub">
                        <div className="lead-list-view-details-view-box-full-info-data-section-main">
                          Organisation
                        </div>
                        <div className="lead-list-view-details-view-box-full-info-data-section-sub">
                          <TextInputComponent
                            type="text"
                            fieldName="company"
                            placeholder="Enter Organisation"
                            optional={false}
                            disabled={checkDisableField()}
                            value={values.company}
                            onChange={(e: any) =>
                              setFieldValue("company", e?.target?.value)
                            }
                            maxlength="100"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="lead-list-view-details-view-box-full-info-header-text">
                    Description
                  </div>
                  <div className="lead-list-view-details-view-box-full-info-description-box">
                    <DescriptionInputComponent
                      fieldName="description"
                      placeholder="Add Description"
                      optional={false}
                      disabled={checkDisableField()}
                      value={values.description}
                      onChange={(e: any) =>
                        setFieldValue("description", e?.target?.value)
                      }
                      maxlength={200}
                    />
                  </div>
                </>
              ) : (
                ""
              )}
            </div>
            <Box className="footer">
              <button
                type="button"
                className="cancel-button"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="cancel-button next-button-wrapper"
                disabled={!(isFormDirty || uploadedImageFile || removeImage)}
              >
                Save
              </button>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default LeadViewEditViewFullInfo;
